import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input, Label, Select } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Link2, Copy, RefreshCw, Ban, Check, X, Eye, Loader2, ClipboardList, Mail, Send, ChevronRight } from 'lucide-react';

interface LinkState { id?: string; active: boolean; token?: string; client_id?: string; client_name?: string; broker_id?: string; broker_name?: string; template_name?: string; uses?: number; views?: number }
interface Sub { id: string; client_id?: string; broker_id?: string; client_name: string; broker_name?: string; template_name: string; site: Record<string, string>; status: string; answer_count: number; submitted_at: string; reject_reason?: string }
interface Lookup { id: string; name: string; trading_name?: string; broker_id?: string }
interface Broker { id: string; brokerage_name: string }

const errMsg = (e: unknown, fb: string) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fb;
const intakeUrl = (token: string) => `${window.location.origin}/intake/${token}`;

export const PendingSitesPage: React.FC = () => {
  const [clients, setClients] = useState<Lookup[]>([]);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [templates, setTemplates] = useState<Lookup[]>([]);
  const [brokerId, setBrokerId] = useState('');
  const [clientId, setClientId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [links, setLinks] = useState<LinkState[]>([]);
  const [emailOpen, setEmailOpen] = useState<string | null>(null);
  const [emailAddr, setEmailAddr] = useState('');
  const [busy, setBusy] = useState(false);

  const [subs, setSubs] = useState<Sub[]>([]);
  const [fBroker, setFBroker] = useState('');
  const [fClient, setFClient] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<(Sub & { answers?: Array<{ question_code: string; value: string }> }) | null>(null);
  const [rejecting, setRejecting] = useState<Sub | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const [c, t, b] = await Promise.all([
        api.get<Lookup[]>('/clients?limit=10000'),
        api.get<Lookup[]>('/templates'),
        api.get<Broker[]>('/brokers'),
      ]);
      setClients((c.data || []).sort((a, b2) => (a.trading_name || a.name).localeCompare(b2.trading_name || b2.name)));
      setTemplates((t.data || []).map((x) => ({ id: x.id, name: x.name })));
      setBrokers((b.data || []).slice().sort((a, b2) => (a.brokerage_name || '').localeCompare(b2.brokerage_name || '')));
    })();
  }, []);

  const filteredClients = useMemo(
    () => (brokerId ? clients.filter((c) => c.broker_id === brokerId) : clients),
    [brokerId, clients],
  );

  const filteredSubs = useMemo(
    () => subs.filter((s) => (!fBroker || s.broker_id === fBroker) && (!fClient || s.client_id === fClient)),
    [subs, fBroker, fClient],
  );
  const filteredLinks = useMemo(
    () => links.filter((l) => (!fBroker || l.broker_id === fBroker) && (!fClient || l.client_id === fClient)),
    [links, fBroker, fClient],
  );
  // Shared filter options drawn from BOTH active links and pending submissions.
  const filterBrokers = useMemo(() => {
    const m = new Map<string, string>();
    [...links, ...subs].forEach((x) => { if (x.broker_id) m.set(x.broker_id, x.broker_name || x.broker_id); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [links, subs]);
  const filterClients = useMemo(() => {
    const m = new Map<string, string>();
    [...links, ...subs].filter((x) => !fBroker || x.broker_id === fBroker)
      .forEach((x) => { if (x.client_id) m.set(x.client_id, x.client_name || x.client_id); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [links, subs, fBroker]);

  const loadSubs = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get<Sub[]>('/site-intake/submissions?status=pending'); setSubs(r.data); setSel(new Set()); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void loadSubs(); }, [loadSubs]);

  const loadLinks = useCallback(async () => {
    try { const r = await api.get<LinkState[]>('/site-intake/links'); setLinks(r.data || []); }
    catch { setLinks([]); }
  }, []);
  useEffect(() => { void loadLinks(); }, [loadLinks]);

  const generate = async (): Promise<void> => {
    if (!clientId || !templateId) { toast.error('Pick a client and a survey.'); return; }
    setBusy(true);
    try {
      await api.post('/site-intake/links', { client_id: clientId, template_id: templateId });
      toast.success('Link created.');
      await loadLinks();
    } catch (e) { toast.error(errMsg(e, 'Could not create link.')); }
    finally { setBusy(false); }
  };
  const sendLink = async (id?: string): Promise<void> => {
    if (!id) return;
    const emails = emailAddr.split(',').map((e) => e.trim()).filter(Boolean);
    if (!emails.length) { toast.error('Add at least one email address.'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ emailed_to?: string[] }>(`/site-intake/links/${id}/email`, { emails });
      toast.success(`Link emailed to ${r.data.emailed_to?.length || emails.length} recipient(s).`);
      setEmailOpen(null); setEmailAddr('');
    } catch (e) { toast.error(errMsg(e, 'Could not send email.')); } finally { setBusy(false); }
  };
  const regenerate = async (id?: string): Promise<void> => {
    if (!id) return;
    setBusy(true);
    try { await api.post(`/site-intake/links/${id}/regenerate`); toast.success('New link generated — the old one no longer works.'); await loadLinks(); }
    catch (e) { toast.error(errMsg(e, 'Could not regenerate.')); } finally { setBusy(false); }
  };
  const revoke = async (id?: string): Promise<void> => {
    if (!id) return;
    setBusy(true);
    try { await api.post(`/site-intake/links/${id}/revoke`); toast.success('Link stopped — it can no longer be used.'); await loadLinks(); }
    catch (e) { toast.error(errMsg(e, 'Could not stop link.')); } finally { setBusy(false); }
  };

  const approve = async (id: string): Promise<void> => {
    try { await api.post(`/site-intake/submissions/${id}/approve`); toast.success('Approved — site created.'); await loadSubs(); setDetail(null); }
    catch (e) { toast.error(errMsg(e, 'Approve failed.')); }
  };
  const bulkApprove = async (): Promise<void> => {
    if (sel.size === 0) return;
    try { const r = await api.post<{ count: number }>('/site-intake/submissions/bulk-approve', { ids: Array.from(sel) }); toast.success(`${r.data.count} site(s) created.`); await loadSubs(); }
    catch (e) { toast.error(errMsg(e, 'Bulk approve failed.')); }
  };
  const doReject = async (): Promise<void> => {
    if (!rejecting || !reason.trim()) { toast.error('Enter a reason.'); return; }
    try { await api.post(`/site-intake/submissions/${rejecting.id}/reject`, { reason: reason.trim() }); toast.success('Rejected.'); setRejecting(null); setReason(''); await loadSubs(); }
    catch (e) { toast.error(errMsg(e, 'Reject failed.')); }
  };
  const openDetail = async (id: string): Promise<void> => {
    const r = await api.get(`/site-intake/submissions/${id}`); setDetail(r.data);
  };
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="space-y-5 animate-fade-in" data-testid="pending-sites">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Pending Sites</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Send a client a link to capture their sites, then review and approve each submission into a site.</p>
      </div>

      {/* Intake link manager */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Link2 size={18} className="text-raptor-blue" /> Site intake link</CardTitle>
          <CardDescription>Pick a client and the survey the site contact must complete, then share the link.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Broker</Label>
              <Select value={brokerId} onChange={(e) => { setBrokerId(e.target.value); setClientId(''); }} data-testid="intake-broker-select">
                <option value="">All brokers</option>
                {brokers.map((b) => <option key={b.id} value={b.id}>{b.brokerage_name}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Client</Label>
              <Select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="intake-client-select">
                <option value="">{brokerId && filteredClients.length === 0 ? 'No clients for this broker' : 'Select a client…'}</option>
                {filteredClients.map((c) => <option key={c.id} value={c.id}>{c.trading_name || c.name}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Survey to complete</Label>
              <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="intake-template-select">
                <option value="">Select a survey…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => void generate()} disabled={busy || !clientId || !templateId} data-testid="intake-generate">
              Generate link
            </Button>
          </div>

          {(links.length > 0 || subs.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/50" data-testid="intake-filter-bar">
              <span className="text-[11px] uppercase tracking-wide text-raptor-navy/50">Filter</span>
              <Select value={fBroker} onChange={(e) => { setFBroker(e.target.value); setFClient(''); }} className="h-8 text-xs w-auto min-w-[150px]" data-testid="filter-broker">
                <option value="">All brokers</option>
                {filterBrokers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
              <Select value={fClient} onChange={(e) => setFClient(e.target.value)} className="h-8 text-xs w-auto min-w-[150px]" data-testid="filter-client">
                <option value="">All clients</option>
                {filterClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
              {(fBroker || fClient) && <Button size="sm" variant="ghost" onClick={() => { setFBroker(''); setFClient(''); }} data-testid="filter-clear">Reset filters</Button>}
              <span className="text-[11px] text-raptor-navy/40 ml-auto">applies to active links & pending sites</span>
            </div>
          )}

          {links.length > 0 && (
            <div className="space-y-1.5 pt-1" data-testid="intake-links-list">
              <div className="text-[10px] uppercase tracking-wide text-raptor-navy/50">Active links · {filteredLinks.length}{filteredLinks.length !== links.length ? ` of ${links.length}` : ''}</div>
              {filteredLinks.map((l) => (
                <div key={l.id} className="rounded-md border border-border/70 bg-raptor-neutral/20" data-testid={`intake-link-${l.id}`}>
                  <div className="flex items-center gap-2 px-2.5 py-1.5 text-sm">
                    <span className="text-raptor-navy/60 truncate max-w-[110px] shrink-0" title={l.broker_name || 'Direct'}>{l.broker_name || 'Direct'}</span>
                    <ChevronRight size={12} className="text-raptor-navy/30 shrink-0" />
                    <span className="font-medium text-raptor-navy truncate max-w-[130px]" title={l.client_name || ''}>{l.client_name || '—'}</span>
                    <Badge variant="outline" className="shrink-0 text-[11px]" title="Survey to complete">{l.template_name || 'Survey'}</Badge>
                    <span className="flex items-center gap-2 text-[11px] text-raptor-navy/60 font-tabular shrink-0">
                      <span>Clicks <b className="text-raptor-navy">{l.views || 0}</b></span>
                      <span>Submitted <b className="text-raptor-navy">{l.uses || 0}</b></span>
                    </span>
                    <span className="ml-auto flex items-center gap-0.5 shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Copy link" onClick={() => { navigator.clipboard?.writeText(intakeUrl(l.token!)); toast.success('Copied'); }} data-testid={`intake-copy-${l.id}`}><Copy size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Email link" onClick={() => { setEmailOpen(emailOpen === l.id ? null : (l.id || null)); setEmailAddr(''); }} data-testid={`intake-email-${l.id}`}><Mail size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Regenerate" onClick={() => void regenerate(l.id)} disabled={busy} data-testid={`intake-regenerate-${l.id}`}><RefreshCw size={13} /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" title="Stop link" onClick={() => void revoke(l.id)} disabled={busy} data-testid={`intake-revoke-${l.id}`}><Ban size={13} /></Button>
                    </span>
                  </div>
                  {emailOpen === l.id && (
                    <div className="flex items-center gap-2 px-2.5 pb-2 pt-0.5 border-t border-border/50" data-testid={`intake-email-box-${l.id}`}>
                      <Input autoFocus value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} placeholder="email@example.com, another@example.com" className="text-xs h-8" data-testid={`intake-email-input-${l.id}`} />
                      <Button size="sm" variant="gradient" disabled={busy} onClick={() => void sendLink(l.id)} data-testid={`intake-email-send-${l.id}`}><Send size={13} /> Send</Button>
                    </div>
                  )}
                </div>
              ))}
              {filteredLinks.length === 0 && <p className="text-xs text-raptor-navy/50">No active links match this filter.</p>}
            </div>
          )}
          {links.length === 0 && <p className="text-xs text-raptor-navy/50">No active links yet. Pick a client and survey above to create one.</p>}
        </CardContent>
      </Card>

      {/* Approval queue */}
      <Card>
        <CardHeader><CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2"><ClipboardList size={18} className="text-raptor-blue" /> Awaiting approval · {filteredSubs.length}{filteredSubs.length !== subs.length ? ` of ${subs.length}` : ''}</span>
          <span className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => void loadSubs()}><RefreshCw size={14} /> Refresh</Button>
            <Button size="sm" variant="gradient" disabled={sel.size === 0} onClick={() => void bulkApprove()} data-testid="bulk-approve"><Check size={14} /> Approve selected ({sel.size})</Button>
          </span>
        </CardTitle></CardHeader>
        <CardContent className="p-0 overflow-auto">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
              <tr>
                <th className="py-2 px-3 w-8"></th>
                <th className="py-2 px-3 text-left">Site</th>
                <th className="py-2 px-3 text-left">Broker</th>
                <th className="py-2 px-3 text-left">Client</th>
                <th className="py-2 px-3 text-left">Address</th>
                <th className="py-2 px-3 text-left">Survey</th>
                <th className="py-2 px-3 text-left">Submitted</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="py-8 text-center text-raptor-navy/50"><Loader2 className="inline animate-spin" /> Loading…</td></tr>}
              {!loading && filteredSubs.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-raptor-navy/50">{subs.length === 0 ? 'No submissions awaiting approval.' : 'No submissions match these filters.'}</td></tr>}
              {filteredSubs.map((s) => (
                <tr key={s.id} className="border-t border-border/60" data-testid={`sub-row-${s.id}`}>
                  <td className="py-2 px-3"><input type="checkbox" checked={sel.has(s.id)} onChange={() => toggle(s.id)} data-testid={`sub-check-${s.id}`} /></td>
                  <td className="py-2 px-3 font-medium text-raptor-navy">{s.site?.name || '—'}</td>
                  <td className="py-2 px-3 text-xs" data-testid={`sub-broker-${s.id}`}>{s.broker_name || <span className="text-raptor-navy/40">Direct</span>}</td>
                  <td className="py-2 px-3">{s.client_name}</td>
                  <td className="py-2 px-3 text-xs text-raptor-navy/70">{s.site?.physical_address || '—'}</td>
                  <td className="py-2 px-3 text-xs">{s.template_name} · {s.answer_count} ans</td>
                  <td className="py-2 px-3 font-tabular text-xs">{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '—'}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => void openDetail(s.id)} data-testid={`sub-view-${s.id}`}><Eye size={13} /></Button>
                      <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200" onClick={() => void approve(s.id)} data-testid={`sub-approve-${s.id}`}><Check size={13} /> Approve</Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => { setRejecting(s); setReason(''); }} data-testid={`sub-reject-${s.id}`}><X size={13} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="sub-detail">
            <div className="p-4 border-b border-border/60 flex items-center justify-between">
              <div>
                <div className="font-headline text-raptor-navy">{detail.site?.name}</div>
                <div className="text-xs text-raptor-navy/60 mt-0.5 flex items-center gap-1.5" data-testid="sub-detail-assoc">
                  <Badge variant="outline" className="text-[11px]">{detail.broker_name || 'Direct'}</Badge>
                  <ChevronRight size={12} className="text-raptor-navy/40" />
                  <span className="font-medium">{detail.client_name}</span>
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setDetail(null)}><X size={16} /></Button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div>
                <div className="text-[10px] uppercase text-raptor-navy/50 mb-1">Site details</div>
                {Object.entries(detail.site || {}).map(([k, v]) => v ? <div key={k} className="flex justify-between gap-3"><span className="text-raptor-navy/60">{k}</span><span className="text-raptor-navy text-right">{v}</span></div> : null)}
              </div>
              <div>
                <div className="text-[10px] uppercase text-raptor-navy/50 mb-1">Survey answers ({detail.answers?.length || 0})</div>
                {(detail.answers || []).map((a, i) => <div key={i} className="flex justify-between gap-3 border-t border-border/40 py-1"><span className="text-raptor-navy/60">{a.question_code}</span><span className="text-raptor-navy text-right">{String(a.value)}</span></div>)}
                {(detail.answers?.length || 0) === 0 && <div className="text-xs text-raptor-navy/40">No answers recorded.</div>}
              </div>
            </div>
            <div className="p-4 border-t border-border/60 flex justify-end gap-2">
              <Button variant="outline" className="text-red-600 border-red-200" onClick={() => { setRejecting(detail); setReason(''); setDetail(null); }}><X size={14} /> Reject</Button>
              <Button variant="gradient" onClick={() => void approve(detail.id)}><Check size={14} /> Approve &amp; create site</Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejecting && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRejecting(null)}>
          <div className="bg-white rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
            <div className="p-4 space-y-3">
              <div className="font-headline text-raptor-navy">Reject “{rejecting.site?.name}”</div>
              <div className="space-y-1.5"><Label>Reason (shared internally)</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. duplicate / incomplete address" data-testid="reject-reason" /></div>
            </div>
            <div className="p-4 border-t border-border/60 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => void doReject()} data-testid="reject-confirm">Reject submission</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingSitesPage;
